import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Tag, Avatar, Space, Button, Divider,
  Table, Badge, Skeleton, message, Image, Tabs, Upload,
  Modal, Form, Input, Select, DatePicker, Popconfirm, Timeline,
} from 'antd';
import {
  UserOutlined, HomeOutlined, ArrowLeftOutlined,
  CarOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined,
  FileProtectOutlined, CheckCircleOutlined, ClockCircleOutlined,
  IdcardOutlined, SafetyCertificateOutlined, WifiOutlined,
  PlusOutlined, DeleteOutlined, HistoryOutlined, FileTextOutlined,
  DollarOutlined, ToolOutlined, EditOutlined, TeamOutlined, WhatsAppOutlined,
  GlobalOutlined, StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import FotoUpload from '../components/FotoUpload';
import Footer from '../components/Footer';

const TIPO_HISTORIAL = {
  LLAMADA:      { color: 'blue',    label: 'Llamada',         icon: <PhoneOutlined /> },
  WHATSAPP:     { color: 'green',   label: 'WhatsApp',        icon: <WhatsAppOutlined /> },
  VISITA:       { color: 'cyan',    label: 'Visita',          icon: <TeamOutlined /> },
  EMAIL:        { color: 'purple',  label: 'Email',           icon: <MailOutlined /> },
  PROMESA_PAGO: { color: 'gold',    label: 'Promesa de pago', icon: <DollarOutlined /> },
  NOTA:         { color: 'default', label: 'Nota interna',    icon: <EditOutlined /> },
  SOPORTE:      { color: 'cyan',    label: 'Soporte',         icon: <ToolOutlined /> },
};

const ESTADO_PROMESA = {
  PENDIENTE:   { color: 'orange', label: 'Pendiente' },
  CUMPLIDA:    { color: 'green',  label: 'Cumplida'  },
  INCUMPLIDA:  { color: 'red',    label: 'Incumplida'},
};

const BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
  : 'http://localhost:5001';

const SERVICIO_CONFIG = {
  RASTREO:        { color: hssColors.primary,   label: 'Rastreo',          icon: <WifiOutlined /> },
  VISION:         { color: '#8e44ad',            label: 'HSS Vision',       icon: <SafetyCertificateOutlined /> },
  RASTREO_VISION: { color: '#16a085',            label: 'Rastreo + Vision', icon: <SafetyCertificateOutlined /> },
};

const TIPO_ICONO = {
  camion: '🚛', camioneta: '🚙', auto: '🚗', moto: '🏍️',
  tractor: '🚜', barco: '⛵', avion: '✈️',
};

const TIPO_ORDEN_COLOR = {
  Instalacion: 'blue', Desinstalacion: 'red', Service: 'green',
  Soporte: 'cyan', Interno: 'purple', Desarrollo: 'orange',
};
const PRIORIDAD_COLOR = { BAJA: 'default', MEDIA: 'blue', ALTA: 'orange', URGENTE: 'red' };

const ClienteDetalle = () => {
  const { dni } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [usuariosCliente, setUsuariosCliente] = useState([]);
  const [modalLog, setModalLog] = useState(false);
  const [formLog] = Form.useForm();
  const [tipoLog, setTipoLog] = useState('LLAMADA');
  const [savingLog, setSavingLog] = useState(false);
  const [contactosEmergencia, setContactosEmergencia] = useState([]);
  const [modalContacto, setModalContacto] = useState(false);
  const [formContacto] = Form.useForm();
  const [contactoEditando, setContactoEditando] = useState(null);
  const [modalEditarCliente, setModalEditarCliente] = useState(false);
  const [formEditarCliente] = Form.useForm();
  const [savingCliente, setSavingCliente] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [modalEstado, setModalEstado] = useState(false);
  const [estadoNuevo, setEstadoNuevo] = useState('activo');
  const [motivoEstado, setMotivoEstado] = useState('');
  const [savingEstado, setSavingEstado] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/clientes/${dni}`);
      setCliente(res.data);
    } catch {
      message.error('No se pudo cargar el cliente');
    } finally {
      setLoading(false);
    }
  }, [dni]);

  const cargarHistorial = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/clientes/${dni}/historial`);
      setHistorial(res.data);
    } catch {}
  }, [dni]);

  const cargarOrdenes = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/clientes/${dni}/ordenes`);
      setOrdenes(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, [dni]);

  const cargarUsuarios = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/usuarios-clientes?cliente_dni=${dni}`);
      setUsuariosCliente(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, [dni]);

  const cargarContactosEmergencia = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/clientes/${dni}/contactos-emergencia`);
      setContactosEmergencia(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, [dni]);

  const cargarDocumentos = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/clientes/${dni}/documentos`);
      setDocumentos(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, [dni]);

  useEffect(() => { cargar(); cargarHistorial(); cargarOrdenes(); cargarUsuarios(); cargarContactosEmergencia(); cargarDocumentos(); }, [cargar, cargarHistorial, cargarOrdenes, cargarUsuarios, cargarContactosEmergencia, cargarDocumentos]);

  const subirDocumento = async (file) => {
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('nombre', file.name);
    try {
      await axiosInstance.post(`/clientes/${dni}/documentos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('Documento subido');
      cargarDocumentos();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al subir documento');
    } finally { setUploadingDoc(false); }
    return false;
  };

  const eliminarDocumento = async (docId) => {
    try {
      await axiosInstance.delete(`/clientes/${dni}/documentos/${docId}`);
      message.success('Documento eliminado');
      cargarDocumentos();
    } catch { message.error('Error al eliminar'); }
  };

  const abrirModalEstado = () => {
    setEstadoNuevo(cliente?.estado || 'activo');
    setMotivoEstado('');
    setModalEstado(true);
  };

  const guardarEstado = async () => {
    if (estadoNuevo !== 'activo' && !motivoEstado.trim()) {
      message.warning('El motivo es obligatorio al cambiar a Inactivo o Baja');
      return;
    }
    setSavingEstado(true);
    try {
      await axiosInstance.put(`/clientes/${dni}/estado`, { estado: estadoNuevo, motivo: motivoEstado.trim() });
      message.success('Estado actualizado');
      setModalEstado(false);
      cargar();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al cambiar estado');
    } finally { setSavingEstado(false); }
  };

  const ESTADO_TAG = {
    activo:   { color: 'success', label: 'Activo' },
    inactivo: { color: 'warning', label: 'Inactivo' },
    baja:     { color: 'error',   label: 'Dado de baja' },
  };

  const guardarLog = async (values) => {
    setSavingLog(true);
    try {
      await axiosInstance.post(`/clientes/${dni}/historial`, {
        tipo: values.tipo,
        descripcion: values.descripcion,
        fecha: values.fecha ? values.fecha.toISOString() : new Date().toISOString(),
        monto: values.monto || null,
        estado_promesa: values.tipo === 'PROMESA_PAGO' ? (values.estado_promesa || 'PENDIENTE') : null,
      });
      message.success('Entrada registrada');
      setModalLog(false);
      formLog.resetFields();
      cargarHistorial();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al guardar');
    } finally { setSavingLog(false); }
  };

  const eliminarLog = async (logId) => {
    try {
      await axiosInstance.delete(`/clientes/${dni}/historial/${logId}`);
      message.success('Eliminado');
      cargarHistorial();
    } catch { message.error('Error al eliminar'); }
  };

  const cambiarEstadoPromesa = async (log, estado) => {
    try {
      await axiosInstance.put(`/clientes/${dni}/historial/${log.id}`, { estado_promesa: estado });
      cargarHistorial();
    } catch { message.error('Error al actualizar'); }
  };

  const abrirEditarCliente = () => {
    formEditarCliente.setFieldsValue({
      celular: cliente.celular || '',
      email: cliente.email || '',
      domicilio: cliente.domicilio || '',
      ruc: cliente.ruc || '',
      facturacion_nombre: cliente.facturacion_nombre || '',
      facturacion_ruc: cliente.facturacion_ruc || '',
    });
    setModalEditarCliente(true);
  };

  const guardarCliente = async (values) => {
    setSavingCliente(true);
    try {
      await axiosInstance.put(`/clientes/${dni}`, values);
      message.success('Datos actualizados');
      setModalEditarCliente(false);
      cargar();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al guardar');
    } finally { setSavingCliente(false); }
  };

  const guardarContacto = async (values) => {
    try {
      if (contactoEditando) {
        await axiosInstance.put(`/clientes/${dni}/contactos-emergencia/${contactoEditando.id}`, values);
      } else {
        await axiosInstance.post(`/clientes/${dni}/contactos-emergencia`, values);
      }
      message.success(contactoEditando ? 'Contacto actualizado' : 'Contacto agregado');
      setModalContacto(false);
      formContacto.resetFields();
      setContactoEditando(null);
      cargarContactosEmergencia();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al guardar');
    }
  };

  const eliminarContacto = async (id) => {
    try {
      await axiosInstance.delete(`/clientes/${dni}/contactos-emergencia/${id}`);
      message.success('Contacto eliminado');
      cargarContactosEmergencia();
    } catch { message.error('Error al eliminar'); }
  };

  const abrirEditarContacto = (c) => {
    setContactoEditando(c);
    formContacto.setFieldsValue({ nombre: c.nombre, relacion: c.relacion, celular: c.celular, email: c.email });
    setModalContacto(true);
  };

  const columnsOrdenes = [
    {
      title: '#', dataIndex: 'numero_orden', key: 'num', width: 70,
      render: n => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: hssColors.primary }}>#{n}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 130,
      render: t => <Tag color={TIPO_ORDEN_COLOR[t] || 'default'}>{t}</Tag>,
    },
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado', width: 130,
      render: e => {
        if (e === 'ACTIVO') return <Tag icon={<ClockCircleOutlined />} color="processing">Activo</Tag>;
        if (e === 'COMPLETADO') return <Tag icon={<CheckCircleOutlined />} color="success">Completado</Tag>;
        return <Tag color="error">Cancelado</Tag>;
      },
    },
    {
      title: 'Prioridad', dataIndex: 'prioridad', key: 'prioridad', width: 110,
      render: p => <Tag color={PRIORIDAD_COLOR[p] || 'default'}>{p}</Tag>,
    },
    {
      title: 'Vehículo', dataIndex: 'vehiculo_chapa', key: 'vehiculo', width: 120,
      render: chapa => chapa
        ? <Tag color="blue" style={{ cursor: 'pointer', fontFamily: 'monospace', fontWeight: 600 }} onClick={() => navigate(`/vehiculos/${chapa}`)}>{chapa}</Tag>
        : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Responsable', dataIndex: 'responsable', key: 'responsable',
      render: r => r?.nombre || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Observaciones', dataIndex: 'observaciones', key: 'obs',
      render: obs => obs ? <span style={{ fontSize: 13, color: hssColors.textSecondary }}>{obs}</span> : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Fecha', dataIndex: 'fecha_creacion', key: 'fecha', width: 110,
      render: f => f ? new Date(f).toLocaleDateString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—',
    },
  ];

  const columnsUsuarios = [
    {
      title: 'Usuario', dataIndex: 'usuario', key: 'usuario', width: 180,
      render: u => <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 14 }}>{u}</span>,
    },
    {
      title: 'Vehículos con acceso', dataIndex: 'vehiculos', key: 'vehiculos',
      render: vs => vs && vs.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {vs.map(v => (
            <Tag
              key={v.chapa}
              color="blue"
              style={{ cursor: 'pointer', fontFamily: 'monospace', fontWeight: 600, margin: 0 }}
              onClick={() => navigate(`/vehiculos/${v.chapa}`)}
            >
              {v.chapa}
              <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 4 }}>{v.marca} {v.modelo}</span>
            </Tag>
          ))}
        </div>
      ) : <span style={{ color: hssColors.gray400 }}>Sin vehículos asignados</span>,
    },
    {
      title: 'Observaciones', dataIndex: 'observaciones', key: 'obs',
      render: o => o ? <span style={{ fontSize: 13, color: hssColors.textSecondary }}>{o}</span> : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Fecha alta', dataIndex: 'created_at', key: 'created_at', width: 120,
      render: d => d ? new Date(d).toLocaleDateString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—',
    },
  ];

  const columnsVehiculos = [
    {
      title: 'Chapa', dataIndex: 'chapa', key: 'chapa',
      render: chapa => (
        <span
          style={{ fontWeight: 700, color: hssColors.primary, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'monospace', fontSize: 14 }}
          onClick={() => navigate(`/vehiculos/${chapa}`)}
        >
          {chapa}
        </span>
      ),
    },
    {
      title: 'Vehículo', key: 'vehiculo',
      render: (_, v) => (
        <Space size={8}>
          <span style={{ fontSize: 18 }}>{TIPO_ICONO[v.tipo?.toLowerCase()] || '🚗'}</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{v.marca} {v.modelo}</div>
            {v.ano && <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{v.ano} · {v.color}</div>}
          </div>
        </Space>
      ),
    },
    {
      title: 'Servicio', dataIndex: 'servicio', key: 'servicio',
      render: s => {
        const cfg = SERVICIO_CONFIG[s];
        if (!cfg) return <Tag color="default">Sin servicio</Tag>;
        return (
          <Tag icon={cfg.icon} style={{ background: cfg.color, color: '#fff', border: 'none', fontWeight: 600, fontSize: 13 }}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'GPS', dataIndex: 'gps', key: 'gps',
      render: g => g ? <Tag color="blue">{g}</Tag> : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Órdenes', key: 'ordenes',
      render: (_, v) => (
        <Space>
          <Badge count={v.ordenes_activas} style={{ background: hssColors.warning }} />
          <span style={{ fontSize: 13, color: hssColors.textSecondary }}>{v.total_ordenes} total</span>
        </Space>
      ),
    },
    {
      title: '', key: 'accion',
      render: (_, v) => (
        <Button size="small" type="link" onClick={() => navigate(`/vehiculos/${v.chapa}`)}>
          Ver detalle →
        </Button>
      ),
    },
  ];

  const columnsContratos = [
    {
      title: 'ID', dataIndex: 'id', key: 'id',
      render: id => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: hssColors.textSecondary }}>
          #{id?.slice(0, 8).toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Tipo', dataIndex: 'tipo_contrato', key: 'tipo',
      render: t => <Tag color={t === 'RASTREO' ? 'blue' : 'purple'}>{t}</Tag>,
    },
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado',
      render: e => e === 'FIRMADO'
        ? <Tag icon={<CheckCircleOutlined />} color="success">Firmado</Tag>
        : <Tag icon={<ClockCircleOutlined />} color="warning">Pendiente</Tag>,
    },
    {
      title: 'Vigencia', dataIndex: 'vigencia', key: 'vigencia',
      render: v => v || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Monto', dataIndex: 'servicio_monto', key: 'monto',
      render: m => <span style={{ fontWeight: 600 }}>{m}</span>,
    },
    {
      title: 'Vehículos', dataIndex: 'vehiculos', key: 'vehiculos',
      render: chapas => (
        <Space wrap size={4}>
          {chapas?.map(c => (
            <Tag
              key={c}
              style={{ cursor: 'pointer', fontFamily: 'monospace', fontWeight: 600 }}
              color="blue"
              onClick={() => navigate(`/vehiculos/${c}`)}
            >
              {c}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Fecha firma', dataIndex: 'signed_at', key: 'signed_at',
      render: d => d
        ? new Date(d).toLocaleDateString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : <span style={{ color: hssColors.gray400 }}>Sin firmar</span>,
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, padding: '24px 32px' }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (!cliente) return null;

  const { vehiculos = [], contratos = [] } = cliente;
  const serviciosActivos = vehiculos.filter(v => v.servicio).length;
  const vehiculosConOrdenActiva = vehiculos.filter(v => v.ordenes_activas > 0).length;
  const contratosFirmados = contratos.filter(c => c.estado === 'FIRMADO').length;

  const fotoSrc = cliente.foto_cedula
    ? (cliente.foto_cedula.startsWith('http') ? cliente.foto_cedula : `${BASE}${cliente.foto_cedula}`)
    : null;

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar size={56} style={{ background: hssColors.primary, fontSize: 24, flexShrink: 0 }}>
              {cliente.nombre.charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <Space align="center" style={{ marginBottom: 2 }}>
                <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: hssColors.primary }}>
                  {cliente.nombre} {cliente.apellido}
                </h1>
                {(() => {
                  const est = ESTADO_TAG[cliente.estado || 'activo'] || ESTADO_TAG.activo;
                  return (
                    <Tag
                      color={est.color}
                      style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                      onClick={abrirModalEstado}
                    >
                      {est.label}
                    </Tag>
                  );
                })()}
              </Space>
              <p style={{ margin: 0, fontSize: 14, color: hssColors.textSecondary }}>
                CI: {cliente.dni}{cliente.ruc ? ` · RUC: ${cliente.ruc}` : ''}
              </p>
            </div>
          </div>
          <Space>
            <Button
              icon={<HomeOutlined />}
              onClick={() => navigate('/dashboard')}
              size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}
            >
              Inicio
            </Button>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/clientes')}
              size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}
            >
              Volver a Clientes
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>

        {/* Stats rápidas */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {[
            { label: 'Vehículos', value: vehiculos.length, color: hssColors.primary, icon: <CarOutlined /> },
            { label: 'Servicios activos', value: serviciosActivos, color: hssColors.success, icon: <WifiOutlined /> },
            { label: 'Con orden activa', value: vehiculosConOrdenActiva, color: hssColors.warning, icon: <ClockCircleOutlined /> },
            { label: 'Contratos firmados', value: contratosFirmados, color: hssColors.info, icon: <FileProtectOutlined /> },
          ].map(stat => (
            <Col xs={12} sm={6} key={stat.label}>
              <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${stat.color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 28, color: stat.color }}>{stat.icon}</div>
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: stat.color, lineHeight: 1.1 }}>{stat.value}</div>
                    <div style={{ fontSize: 13, color: hssColors.textSecondary }}>{stat.label}</div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Tabs
          defaultActiveKey="info"
          size="large"
          style={{ background: hssColors.backgroundSecondary, borderRadius: 12, padding: '0 16px', boxShadow: commonStyles.shadowMd }}
          items={[
            {
              key: 'info',
              label: <Space><UserOutlined />Información</Space>,
              children: (
                <div style={{ padding: '16px 0' }}>
                  <Row gutter={[20, 20]}>
                    <Col xs={24} lg={8}>
                      <Card
                        title={<Space><UserOutlined style={{ color: hssColors.primary }} /><span>Datos de contacto</span></Space>}
                        extra={<Button size="small" icon={<EditOutlined />} onClick={abrirEditarCliente}>Editar</Button>}
                        style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm, height: '100%' }}
                      >
              <Space direction="vertical" size={14} style={{ width: '100%' }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <PhoneOutlined style={{ color: hssColors.success, fontSize: 16 }} />
                  <div>
                    <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Celular</div>
                    {cliente.celular
                      ? <a href={`tel:${cliente.celular}`} style={{ fontSize: 15, fontWeight: 600 }}>{cliente.celular}</a>
                      : <span style={{ fontSize: 14, color: hssColors.gray400 }}>Sin datos</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <MailOutlined style={{ color: hssColors.info, fontSize: 16 }} />
                  <div>
                    <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Email</div>
                    {cliente.email
                      ? <a href={`mailto:${cliente.email}`} style={{ fontSize: 15, fontWeight: 600 }}>{cliente.email}</a>
                      : <span style={{ fontSize: 14, color: hssColors.gray400 }}>Sin datos</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <EnvironmentOutlined style={{ color: hssColors.secondary, fontSize: 16, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Domicilio</div>
                    {cliente.domicilio
                      ? <div style={{ fontSize: 15, fontWeight: 600 }}>{cliente.domicilio}</div>
                      : <span style={{ fontSize: 14, color: hssColors.gray400 }}>Sin datos</span>}
                  </div>
                </div>

                {cliente.ruc && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <IdcardOutlined style={{ color: hssColors.warning, fontSize: 16 }} />
                    <div>
                      <div style={{ fontSize: 12, color: hssColors.textSecondary }}>RUC</div>
                      <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'monospace' }}>{cliente.ruc}</div>
                    </div>
                  </div>
                )}

                {/* Datos de facturación diferenciados */}
                {(cliente.facturacion_nombre || cliente.facturacion_ruc) && (
                  <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#f57f17', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      💰 Facturar a nombre de
                    </div>
                    {cliente.facturacion_nombre && (
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{cliente.facturacion_nombre}</div>
                    )}
                    {cliente.facturacion_ruc && (
                      <div style={{ fontSize: 13, color: hssColors.textSecondary, fontFamily: 'monospace' }}>RUC: {cliente.facturacion_ruc}</div>
                    )}
                  </div>
                )}

                <Divider style={{ margin: '8px 0' }} />

                {/* Foto documento de identidad */}
                <div>
                  <div style={{ fontSize: 13, color: hssColors.textSecondary, marginBottom: 10, fontWeight: 600 }}>
                    <IdcardOutlined style={{ marginRight: 6 }} />
                    {cliente.tipo_cliente === 'EMPRESA' ? 'Foto cédula tributaria (RUC)' : 'Foto cédula de identidad'}
                  </div>
                  {fotoSrc ? (
                    <Image
                      src={fotoSrc}
                      alt="Documento"
                      style={{ width: '100%', maxWidth: 260, borderRadius: 8, border: `1px solid ${hssColors.gray200}` }}
                      preview
                    />
                  ) : (
                    <FotoUpload
                      label={cliente.tipo_cliente === 'EMPRESA' ? 'Subir foto cédula tributaria' : 'Subir foto cédula'}
                      urlEndpoint={`/clientes/${dni}/fotos`}
                      deleteEndpoint={`/clientes/${dni}/fotos`}
                      fotoActual={null}
                      onSuccess={cargar}
                      onDelete={cargar}
                    />
                  )}
                  {cliente.tipo_cliente === 'EMPRESA' && (
                    <div style={{ fontSize: 11, color: hssColors.gray400, marginTop: 6 }}>
                      Para actas, constancias y otros documentos usá la pestaña <strong>Documentos</strong>.
                    </div>
                  )}
                </div>

              </Space>
            </Card>
          </Col>

          {/* Servicios activos por tipo */}
          <Col xs={24} lg={16}>
            <Card
              title={<Space><WifiOutlined style={{ color: hssColors.success }} /><span>Servicios contratados</span></Space>}
              style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
            >
              {(() => {
                const RESUMEN_SERVICIOS = [
                  { key: 'RASTREO', label: 'Rastreo Satelital', color: hssColors.primary, icon: <WifiOutlined /> },
                  { key: 'VISION',  label: 'HSS Vision',        color: '#8e44ad',          icon: <SafetyCertificateOutlined /> },
                ];
                const grupos = RESUMEN_SERVICIOS
                  .map(s => ({ ...s, count: vehiculos.filter(v => v.servicio === s.key).length }))
                  .filter(s => s.count > 0);

                const cobertura = cliente.cobertura_nacional && cliente.cobertura_internacional
                  ? 'Nacional e Internacional'
                  : cliente.cobertura_nacional ? 'Nacional'
                  : cliente.cobertura_internacional ? 'Internacional'
                  : null;

                if (grupos.length === 0 && !cobertura) {
                  return (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: hssColors.gray400 }}>
                      Sin servicios contratados
                    </div>
                  );
                }
                return (
                  <Row gutter={[16, 16]}>
                    {grupos.map(s => (
                      <Col xs={24} sm={12} key={s.key}>
                        <div style={{
                          padding: '24px 28px', borderRadius: 12,
                          border: `2px solid ${s.color}33`,
                          background: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        }}>
                          <Space size={12}>
                            <span style={{ color: s.color, fontSize: 22 }}>{s.icon}</span>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#2c3e50' }}>{s.label}</span>
                          </Space>
                          <div style={{
                            background: s.color, color: '#fff', borderRadius: 20,
                            padding: '4px 18px', fontWeight: 700, fontSize: 18,
                          }}>
                            {s.count}
                          </div>
                        </div>
                      </Col>
                    ))}
                    {cobertura && (
                      <Col xs={24} sm={12} key="cobertura">
                        <div style={{
                          padding: '24px 28px', borderRadius: 12,
                          border: `2px solid ${hssColors.success}33`,
                          background: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        }}>
                          <Space size={12}>
                            <span style={{ color: hssColors.success, fontSize: 22 }}><GlobalOutlined /></span>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#2c3e50' }}>Cobertura</span>
                          </Space>
                          <div style={{
                            background: hssColors.success, color: '#fff', borderRadius: 20,
                            padding: '4px 18px', fontWeight: 700, fontSize: 14,
                          }}>
                            {cobertura}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                );
              })()}
            </Card>
          </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: 'vehiculos',
              label: <Space><CarOutlined />Vehículos<Badge count={vehiculos.length} style={{ background: hssColors.primary }} /></Space>,
              children: (
                <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }} styles={{ body: { padding: 0 } }}>
                  <Table
                    columns={columnsVehiculos}
                    dataSource={vehiculos}
                    rowKey="chapa"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    locale={{ emptyText: 'Este cliente no tiene vehículos registrados' }}
                    onRow={v => ({ onClick: () => navigate(`/vehiculos/${v.chapa}`), style: { cursor: 'pointer' } })}
                  />
                </Card>
              ),
            },
            {
              key: 'ordenes',
              label: <Space><FileTextOutlined />Órdenes<Badge count={ordenes.length} style={{ background: '#e67e22' }} /></Space>,
              children: (
                <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }} styles={{ body: { padding: 0 } }}>
                  <Table
                    columns={columnsOrdenes}
                    dataSource={ordenes}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10, showTotal: t => `Total: ${t} órdenes` }}
                    locale={{ emptyText: 'Sin órdenes de trabajo registradas para este cliente' }}
                  />
                </Card>
              ),
            },
            {
              key: 'contratos',
              label: <Space><FileProtectOutlined />Contratos<Badge count={contratos.length} style={{ background: hssColors.info }} /></Space>,
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }}
                  styles={{ body: { padding: 0 } }}
                  extra={<Button type="link" onClick={() => navigate('/contratos')} style={{ fontWeight: 600 }}>Ver todos →</Button>}
                >
                  <Table
                    columns={columnsContratos}
                    dataSource={contratos}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    locale={{ emptyText: 'Este cliente no tiene contratos registrados' }}
                  />
                </Card>
              ),
            },
            {
              key: 'usuarios',
              label: <Space><TeamOutlined />Usuarios<Badge count={usuariosCliente.length} style={{ background: '#8e44ad' }} /></Space>,
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }}
                  styles={{ body: { padding: 0 } }}
                  extra={
                    <Button type="link" onClick={() => navigate('/usuarios-clientes')} style={{ fontWeight: 600 }}>
                      Administrar usuarios →
                    </Button>
                  }
                >
                  <Table
                    columns={columnsUsuarios}
                    dataSource={usuariosCliente}
                    rowKey="id"
                    pagination={false}
                    locale={{ emptyText: 'Este cliente no tiene usuarios de portal registrados' }}
                  />
                </Card>
              ),
            },
            {
              key: 'emergencia',
              label: <Space><PhoneOutlined />Emergencia<Badge count={contactosEmergencia.length} style={{ background: '#e74c3c' }} /></Space>,
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }}
                  styles={{ body: { padding: 0 } }}
                  extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setContactoEditando(null); formContacto.resetFields(); setModalContacto(true); }} style={{ borderRadius: 8, fontWeight: 600 }}>
                      Agregar contacto
                    </Button>
                  }
                >
                  <Table
                    dataSource={contactosEmergencia}
                    rowKey="id"
                    pagination={false}
                    locale={{ emptyText: 'Sin contactos de emergencia registrados' }}
                    columns={[
                      { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', render: n => <span style={{ fontWeight: 600 }}>{n}</span> },
                      { title: 'Relación', dataIndex: 'relacion', key: 'relacion', render: r => r || <span style={{ color: hssColors.gray400 }}>—</span> },
                      {
                        title: 'Celular', dataIndex: 'celular', key: 'celular',
                        render: cel => cel ? (
                          <Space>
                            <PhoneOutlined style={{ color: hssColors.success }} />
                            <a href={`tel:${cel}`}>{cel}</a>
                          </Space>
                        ) : <span style={{ color: hssColors.gray400 }}>—</span>,
                      },
                      {
                        title: 'Email', dataIndex: 'email', key: 'email',
                        render: e => e ? <a href={`mailto:${e}`}>{e}</a> : <span style={{ color: hssColors.gray400 }}>—</span>,
                      },
                      {
                        title: '', key: 'acciones', width: 80,
                        render: (_, c) => (
                          <Space>
                            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEditarContacto(c)} />
                            <Popconfirm title="¿Eliminar este contacto?" onConfirm={() => eliminarContacto(c.id)} okText="Sí" cancelText="No" okButtonProps={{ danger: true }}>
                              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: 'historial',
              label: <Space><HistoryOutlined />Historial<Badge count={historial.length} style={{ background: hssColors.primary }} /></Space>,
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }}
                  extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { formLog.resetFields(); setTipoLog('LLAMADA'); setModalLog(true); }} style={{ borderRadius: 8, fontWeight: 600 }}>
                      Registrar
                    </Button>
                  }
                >
                  {historial.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: hssColors.gray400 }}>
                      Sin registros aún. Usá "Registrar" para anotar llamadas, visitas, promesas de pago, etc.
                    </div>
                  ) : (
                    <Timeline
                      items={historial.map(log => {
                        const cfg = TIPO_HISTORIAL[log.tipo] || TIPO_HISTORIAL.NOTA;
                        const esPromesa = log.tipo === 'PROMESA_PAGO';
                        const estadoCfg = ESTADO_PROMESA[log.estado_promesa];
                        return {
                          color: cfg.color === 'default' ? 'gray' : cfg.color,
                          dot: <span style={{ fontSize: 14 }}>{cfg.icon}</span>,
                          children: (
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Tag color={cfg.color} style={{ fontWeight: 600 }}>{cfg.label}</Tag>
                                {esPromesa && log.monto && (
                                  <Tag color="gold" style={{ fontWeight: 700 }}>Gs. {log.monto}</Tag>
                                )}
                                {esPromesa && estadoCfg && (
                                  <Select
                                    size="small"
                                    value={log.estado_promesa}
                                    onChange={v => cambiarEstadoPromesa(log, v)}
                                    style={{ width: 130 }}
                                    options={Object.entries(ESTADO_PROMESA).map(([k, v]) => ({ value: k, label: <Tag color={v.color} style={{ margin: 0 }}>{v.label}</Tag> }))}
                                  />
                                )}
                                <span style={{ fontSize: 12, color: hssColors.textSecondary }}>
                                  {log.fecha ? new Date(log.fecha).toLocaleString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                  {log.usuario_nombre ? ` · ${log.usuario_nombre}` : ''}
                                </span>
                                <Popconfirm title="¿Eliminar este registro?" onConfirm={() => eliminarLog(log.id)} okText="Sí" cancelText="No" okButtonProps={{ danger: true }}>
                                  <Button type="text" size="small" icon={<DeleteOutlined />} danger style={{ marginLeft: 'auto' }} />
                                </Popconfirm>
                              </div>
                              {log.descripcion && (
                                <div style={{ marginTop: 4, fontSize: 14, color: hssColors.textPrimary, paddingLeft: 2 }}>
                                  {log.descripcion}
                                </div>
                              )}
                            </div>
                          ),
                        };
                      })}
                    />
                  )}
                </Card>
              ),
            },
            {
              key: 'documentos',
              label: <Space><FileTextOutlined />Documentos<Badge count={documentos.length} style={{ background: hssColors.primary }} /></Space>,
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }}
                  extra={
                    <Upload beforeUpload={subirDocumento} showUploadList={false} accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods">
                      <Button type="primary" icon={<PlusOutlined />} loading={uploadingDoc} style={{ borderRadius: 8, fontWeight: 600 }}>
                        Subir documento
                      </Button>
                    </Upload>
                  }
                >
                  {documentos.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: hssColors.gray400 }}>
                      Sin documentos adjuntos. Podés subir PDF, imágenes, Word, Excel, etc.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {documentos.map(doc => {
                        const isImage = ['jpg','jpeg','png','webp'].includes(doc.tipo);
                        const isPdf = doc.tipo === 'pdf';
                        const iconMap = { pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', odt: '📝', ods: '📊' };
                        const icon = isImage ? '🖼️' : (iconMap[doc.tipo] || '📎');
                        const apiBase = process.env.REACT_APP_API_URL
                          ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
                          : 'http://localhost:5001';
                        const url = `${apiBase}${doc.url}`;
                        return (
                          <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                            <span style={{ fontSize: 22 }}>{icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nombre}</div>
                              <div style={{ fontSize: 11, color: hssColors.textSecondary }}>{doc.tipo?.toUpperCase()} · {doc.fecha_subida ? new Date(doc.fecha_subida).toLocaleDateString('es-PY') : ''}</div>
                            </div>
                            <Space>
                              <Button size="small" icon={<FileTextOutlined />} onClick={() => window.open(url, '_blank')}>
                                {isPdf || !isImage ? 'Abrir' : 'Ver'}
                              </Button>
                              <Popconfirm title="¿Eliminar este documento?" onConfirm={() => eliminarDocumento(doc.id)} okText="Sí" cancelText="No">
                                <Button size="small" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            </Space>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              ),
            },
          ]}
        />

        {/* Modal cambio de estado */}
        <Modal
          title={<Space><StopOutlined style={{ color: hssColors.warning }} /><span>Cambiar estado del cliente</span></Space>}
          open={modalEstado}
          onCancel={() => setModalEstado(false)}
          onOk={guardarEstado}
          okText="Confirmar"
          cancelText="Cancelar"
          confirmLoading={savingEstado}
          okButtonProps={{ disabled: estadoNuevo !== 'activo' && !motivoEstado.trim() }}
          width={420}
        >
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Nuevo estado</div>
              <Select value={estadoNuevo} onChange={setEstadoNuevo} style={{ width: '100%' }} size="large">
                <Select.Option value="activo">Activo</Select.Option>
                <Select.Option value="inactivo">Inactivo</Select.Option>
                <Select.Option value="baja">Dado de baja</Select.Option>
              </Select>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Motivo {estadoNuevo !== 'activo' ? <span style={{ color: 'red' }}>*</span> : '(opcional)'}
              </div>
              <Input.TextArea
                value={motivoEstado}
                onChange={e => setMotivoEstado(e.target.value)}
                rows={3}
                placeholder={estadoNuevo === 'baja' ? 'Ej: Canceló el servicio por...' : estadoNuevo === 'inactivo' ? 'Ej: Sin actividad hace...' : 'Motivo (opcional)'}
              />
            </div>
          </div>
        </Modal>

        {/* Modal editar datos de contacto */}
        <Modal
          title={<Space><EditOutlined style={{ color: hssColors.primary }} /><span>Editar datos de contacto</span></Space>}
          open={modalEditarCliente} onCancel={() => setModalEditarCliente(false)} footer={null} width={480}
        >
          <Form form={formEditarCliente} onFinish={guardarCliente} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="celular" label="Celular">
              <Input size="large" prefix={<PhoneOutlined />} placeholder="Ej: 0981 123 456" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input size="large" prefix={<MailOutlined />} placeholder="Ej: contacto@empresa.com" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="domicilio" label="Domicilio">
              <Input size="large" prefix={<EnvironmentOutlined />} placeholder="Ej: Av. España 1234, Asunción" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="ruc" label="RUC">
              <Input size="large" prefix={<IdcardOutlined />} placeholder="Ej: 80108372-9" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Divider orientation="left" style={{ fontSize: 12, color: '#f57f17' }}>
              💰 Datos de facturación (opcional)
            </Divider>
            <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 12, marginTop: -8 }}>
              Completar solo si las facturas deben emitirse a nombre de otra persona o empresa.
            </div>
            <Form.Item name="facturacion_nombre" label="Facturar a nombre de">
              <Input size="large" prefix={<UserOutlined />} placeholder="Ej: María González" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="facturacion_ruc" label="RUC de facturación">
              <Input size="large" prefix={<IdcardOutlined />} placeholder="Ej: 3456789-1" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setModalEditarCliente(false)} size="large">Cancelar</Button>
                <Button type="primary" htmlType="submit" loading={savingCliente} size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                  Guardar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal contacto de emergencia */}
        <Modal
          title={<Space><PhoneOutlined style={{ color: '#e74c3c' }} /><span>{contactoEditando ? 'Editar contacto' : 'Nuevo contacto de emergencia'}</span></Space>}
          open={modalContacto} onCancel={() => { setModalContacto(false); formContacto.resetFields(); setContactoEditando(null); }} footer={null} width={480}
        >
          <Form form={formContacto} onFinish={guardarContacto} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="nombre" label="Nombre completo" rules={[{ required: true, message: 'Ingresá el nombre' }]}>
              <Input size="large" placeholder="Ej: Juan Pérez" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="relacion" label="Relación / Parentesco">
              <Input size="large" placeholder="Ej: Esposa, Socio, Familiar..." style={{ borderRadius: 8 }} />
            </Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item name="celular" label="Celular">
                <Input size="large" placeholder="Ej: 0981 123 456" style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item name="email" label="Email">
                <Input size="large" placeholder="Ej: juan@mail.com" style={{ borderRadius: 8 }} />
              </Form.Item>
            </div>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => { setModalContacto(false); formContacto.resetFields(); setContactoEditando(null); }} size="large">Cancelar</Button>
                <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                  {contactoEditando ? 'Guardar cambios' : 'Agregar'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal nuevo registro */}
        <Modal
          title={<Space><HistoryOutlined style={{ color: hssColors.primary }} /><span>Registrar contacto</span></Space>}
          open={modalLog} onCancel={() => { setModalLog(false); formLog.resetFields(); }} footer={null} width={500}
        >
          <Form form={formLog} onFinish={guardarLog} layout="vertical" style={{ marginTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item name="tipo" label="Tipo" initialValue="LLAMADA" rules={[{ required: true }]}>
                <Select size="large" onChange={v => setTipoLog(v)}>
                  {Object.entries(TIPO_HISTORIAL).map(([k, v]) => (
                    <Select.Option key={k} value={k}><Space>{v.icon}<span>{v.label}</span></Space></Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="fecha" label="Fecha y hora">
                <DatePicker showTime size="large" style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" placeholder="Ahora" />
              </Form.Item>
            </div>
            {tipoLog === 'PROMESA_PAGO' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Form.Item name="monto" label="Monto (Gs.)">
                  <Input size="large" prefix={<DollarOutlined />} placeholder="Ej: 150000" style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item name="estado_promesa" label="Estado" initialValue="PENDIENTE">
                  <Select size="large">
                    {Object.entries(ESTADO_PROMESA).map(([k, v]) => (
                      <Select.Option key={k} value={k}><Tag color={v.color}>{v.label}</Tag></Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            )}
            <Form.Item name="descripcion" label="Descripción / Notas" rules={[{ required: true, message: 'Ingresá una descripción' }]}>
              <Input.TextArea rows={3} placeholder="¿Qué se habló? ¿Qué se acordó?" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => { setModalLog(false); formLog.resetFields(); }} size="large">Cancelar</Button>
                <Button type="primary" htmlType="submit" loading={savingLog} size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                  Guardar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

      </div>
      <Footer />
    </div>
  );
};

export default ClienteDetalle;
